import { Formik } from 'formik'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import FullPageLoading from 'components/FullPageLoading'
import { useQuery, useSlug, useEventSlug } from 'hooks'
import { _, withCapture } from 'utils'
import {
    MapSpaceQuery,
    MapSpaceQueryVariables,
    UpdateMapSpaceInput
} from 'generated/graphql'
import { getNodes } from 'graphql/utils'
import routes from 'routes'
import Settings from './tabs/Settings'
import { mapSpaceQuery } from './graphql/query'
import FormTabs from '../forms/FormTabs'
import { getChangedValues } from '../forms/utils'
import { updateMapSpace } from './graphql/mutation'
import { getEventData } from '../events/eventSlice'
import { showErrorNotification, showNotification } from '../notifications/notificationsSlice'

const transformDataForForm = (data: UpdateMapSpaceInput) => ({
    parent: {
        name: data.parent?.name,
        enableChat: data.parent?.enableChat,
        image: data.parent?.image,
        helpContent: data.parent?.helpContent,
        helpContentType: data.parent?.helpContentType,
        published: data.parent?.published,
    }
})

const MapSetting = () => {
    const dispatch = useDispatch()
    const [isUploading, setIsUploading] = useState(false)
    const eventSlug = useEventSlug()
    const mapSlug = useSlug('mapSlug')
    const [initialValues, setInitialValues] = useState({})

    const { data, requestStatus } = useQuery<MapSpaceQuery, MapSpaceQueryVariables>(
        mapSpaceQuery, { eventSlug, filters: { parent: { slug: mapSlug } } }
    )

    const mapSpaceData = getNodes(data?.event?.mapSpaces)[0]

    useEffect(() => {
        if (mapSpaceData) {
            setInitialValues(transformDataForForm(mapSpaceData))
        }
    }, [mapSpaceData])

    const handleSubmit = withCapture(async (values, { resetForm }) => {
        setIsUploading(true)
        const submitValues = getChangedValues(initialValues, values) as UpdateMapSpaceInput
        const response = await updateMapSpace(mapSpaceData.id, submitValues)
        setIsUploading(false)

        if (!response.data || response.errors.length) {
            dispatch(showErrorNotification(response.errors))
            return
        }

        const newValues = {
            parent: {
                ...values.parent,
                image: response.data?.updateMapSpace?.parent?.image
            }
        }
        resetForm({ values: newValues })
        setInitialValues(newValues)
        dispatch(showNotification({
            type: 'success',
            body: _('form.notification.update_success'),
        }))

        // If the name/published fields are changed, re-fetch event data for Sidebar
        if (submitValues?.parent?.name || submitValues?.parent?.published !== undefined) {
            dispatch(getEventData(eventSlug))
        }

        if (submitValues?.parent?.image) {
            window.location.href = routes.map.getPath({ mapSlug, eventSlug })
        }
    })

    // const settingTab = document.getElementById('tab-forms-tab-settings')
    // if (requestStatus==='succeeded' && settingTab && !mapSpaceData?.parent?.image) {
    //     settingTab.click()
    // }

    if (requestStatus === 'loading' || requestStatus === 'idle') {
        return <FullPageLoading />
    }

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            enableReinitialize
        >
            <>
                <FormTabs
                    tabs={[
                        // Temporarily, hide this tab following https://twoppy.atlassian.net/browse/ORG-1728
                        // {
                        //     id: 'map-areas',
                        //     name: _('map_setting.tabs.map_areas'),
                        //     Component: () => (
                        //         <MapAreas
                        //             items={mapSpaceData?.items}
                        //             callGetMapItems={callGetMapItems}
                        //             requestStatus={requestStatus}
                        //         />
                        //     )
                        // },
                        {
                            id: 'settings',
                            name: _('map_setting.tabs.settings'),
                            Component: Settings
                        },
                    ]}
                    showHelpContent
                    showSpaceOpened
                />
                {isUploading && <FullPageLoading isAbsolute />}
            </>
        </Formik>
    )
}

export default MapSetting
